import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Newspaper, Plus, Edit3, Trash2, RefreshCw, Star, EyeOff, Eye,
  ExternalLink, Play, AlertCircle, CheckCircle, X, Save, Activity, Download,
} from 'lucide-react';
import { getAdminToken } from '../contexts/AuthContext';

// Uses the same auth pattern as the rest of Admin.jsx — pulls a JWT from AuthContext
// instead of reading raw localStorage. Without this, every admin call returns 401
// and the UI shows the empty state.
function authH() {
  return { headers: { Authorization: `Bearer ${getAdminToken() || ''}` } };
}

// ─── Tabs ────────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'sources',  label: 'Sources' },
  { id: 'articles', label: 'Curation' },
  { id: 'log',      label: 'Fetch Log' },
];

export default function AdminNewsPage() {
  const [tab, setTab] = useState('sources');
  const [stats, setStats] = useState(null);

  const loadStats = useCallback(async () => {
    try { const { data } = await axios.get('/api/admin/news/stats', authH()); setStats(data); }
    catch {}
  }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:10 }}>
        <div>
          <h2 style={{ fontFamily:'var(--font-display)', fontSize:16, fontWeight:700, marginBottom:4, display:'flex', alignItems:'center', gap:6 }}>
            <Newspaper size={15} style={{ color:'var(--cyan)' }} /> Industry Pulse — Admin
          </h2>
          <div style={{ fontSize:12, color:'var(--text-muted)' }}>
            Manage RSS sources, curate Editor's Picks, and monitor the news fetch pipeline.
          </div>
        </div>
        {stats && (
          <div style={{ display:'flex', gap:8, fontSize:11, fontFamily:'var(--font-mono)' }}>
            <span style={{ color:'var(--text-muted)' }}>Sources: <strong style={{ color:'var(--text-primary)' }}>{stats.active_sources}</strong>/{stats.active_sources + stats.inactive_sources}</span>
            <span style={{ color:'var(--text-muted)' }}>·</span>
            <span style={{ color:'var(--text-muted)' }}>Articles: <strong style={{ color:'var(--text-primary)' }}>{stats.visible_articles}</strong></span>
            <span style={{ color:'var(--text-muted)' }}>·</span>
            <span style={{ color:'var(--text-muted)' }}>Last 24h: <strong style={{ color:'var(--cyan)' }}>{stats.last_24h}</strong></span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:14, borderBottom:'1px solid var(--border)' }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding:'8px 14px', background:'transparent', border:'none',
              borderBottom:`2px solid ${tab === t.id ? 'var(--cyan)' : 'transparent'}`,
              color: tab === t.id ? 'var(--cyan)' : 'var(--text-secondary)',
              cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'var(--font-mono)',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'sources'  && <SourcesTab  onAnyChange={loadStats} />}
      {tab === 'articles' && <ArticlesTab />}
      {tab === 'log'      && <FetchLogTab />}
    </div>
  );
}

// ─── Sources tab ─────────────────────────────────────────────────────────────
function SourcesTab({ onAnyChange }) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [backfilling, setBackfilling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get('/api/admin/news/sources', authH()); setSources(data); }
    catch { toast.error('Failed to load sources'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function toggleActive(s) {
    try {
      await axios.patch(`/api/admin/news/sources/${s.id}`, { is_active: !s.is_active }, authH());
      toast.success(`${s.name} ${!s.is_active ? 'enabled' : 'disabled'}`);
      load(); onAnyChange?.();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  }

  async function fetchNow(s) {
    const toastId = toast.loading(`Fetching ${s.name}…`);
    try {
      const { data } = await axios.post(`/api/admin/news/sources/${s.id}/fetch`, {}, authH());
      toast.success(`+${data.articles_added} new from ${s.name}`, { id: toastId });
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Fetch failed', { id: toastId }); }
  }

  async function fetchAll() {
    try {
      await axios.post('/api/admin/news/fetch-all', {}, authH());
      toast.success('Fetch started — refresh in ~30s to see results');
    } catch (err) { toast.error('Failed to trigger fetch'); }
  }

  // Triggers backend og:image scraping for all articles still missing an image.
  // Fire-and-forget on the backend — it returns immediately with a queued count.
  async function backfillImages() {
    setBackfilling(true);
    try {
      const { data } = await axios.post('/api/admin/news/backfill-images', { limit: 500 }, authH());
      if (data.queued > 0) {
        toast.success(`Backfilling images for ${data.queued} articles — give it ~1-2 min, then refresh the user-facing Pulse page`);
      } else {
        toast.success('All articles already have images — nothing to backfill');
      }
    } catch (err) { toast.error(err.response?.data?.error || 'Backfill failed'); }
    finally { setBackfilling(false); }
  }

  async function deleteSource(s) {
    if (!window.confirm(`Delete "${s.name}" and all its articles? This cannot be undone.`)) return;
    try {
      await axios.delete(`/api/admin/news/sources/${s.id}`, authH());
      toast.success(`${s.name} deleted`);
      load(); onAnyChange?.();
    } catch (err) { toast.error(err.response?.data?.error || 'Delete failed'); }
  }

  return (
    <div>
      <div style={{ display:'flex', gap:8, marginBottom:14, justifyContent:'flex-end' }}>
        <button className="btn btn-secondary" onClick={backfillImages} disabled={backfilling} title="Scrape og:image for articles missing thumbnails">
          {backfilling
            ? <><RefreshCw size={12} style={{ animation:'spin 1s linear infinite' }}/> Queuing…</>
            : <><Download size={12} /> Backfill Images</>}
        </button>
        <button className="btn btn-secondary" onClick={fetchAll}>
          <RefreshCw size={12} /> Fetch All Now
        </button>
        <button className="btn btn-primary" onClick={() => { setShowAdd(true); setEditing(null); }}>
          <Plus size={12} /> Add Source
        </button>
      </div>

      {(showAdd || editing) && (
        <SourceForm
          source={editing}
          onClose={() => { setShowAdd(false); setEditing(null); }}
          onSaved={() => { setShowAdd(false); setEditing(null); load(); onAnyChange?.(); }}
        />
      )}

      {loading ? <div style={{ textAlign:'center', padding:40 }}><span className="spinner"/></div> : (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Feed URL</th>
                <th style={{ textAlign:'center', width:90 }}>Articles</th>
                <th style={{ width:140 }}>Last Fetched</th>
                <th style={{ width:80, textAlign:'center' }}>Status</th>
                <th style={{ width:200 }}></th>
              </tr>
            </thead>
            <tbody>
              {sources.map(s => (
                <tr key={s.id} style={{ opacity: s.is_active ? 1 : 0.5 }}>
                  <td>
                    <div style={{ fontWeight:600 }}>{s.name}</div>
                    <div style={{ fontSize:10, color:'var(--text-muted)', fontFamily:'var(--font-mono)' }}>{s.slug}</div>
                  </td>
                  <td style={{ fontSize:11, fontFamily:'var(--font-mono)', color:'var(--text-muted)', maxWidth:260 }}>
                    <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={s.feed_url}>
                      {s.feed_url}
                    </div>
                    {s.last_error && (
                      <div style={{ fontSize:10, color:'var(--red)', marginTop:2, display:'flex', alignItems:'center', gap:3 }}>
                        <AlertCircle size={9} /> {s.last_error.slice(0, 80)}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign:'center', fontFamily:'var(--font-mono)', fontSize:12 }}>{s.article_count}</td>
                  <td style={{ fontSize:11, color:'var(--text-muted)' }}>
                    {s.last_fetched_at ? new Date(s.last_fetched_at).toLocaleString() : 'Never'}
                  </td>
                  <td style={{ textAlign:'center' }}>
                    <span
                      className={`badge ${s.is_active ? 'badge-green' : 'badge-muted'}`}
                      style={{ cursor:'pointer', fontSize:10 }}
                      onClick={() => toggleActive(s)}
                      title="Click to toggle"
                    >
                      {s.is_active ? 'Active' : 'Off'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display:'flex', gap:4, justifyContent:'flex-end' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => fetchNow(s)} title="Fetch now">
                        <Play size={10} />
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setEditing(s)} title="Edit">
                        <Edit3 size={10} />
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => deleteSource(s)} title="Delete">
                        <Trash2 size={10} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {sources.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>No sources configured</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SourceForm({ source, onClose, onSaved }) {
  const isEdit = !!source;
  const [form, setForm] = useState({
    name:     source?.name      || '',
    slug:     source?.slug      || '',
    feed_url: source?.feed_url  || '',
    site_url: source?.site_url  || '',
    logo_url: source?.logo_url  || '',
    is_active: source ? source.is_active : true,
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form.name.trim() || !form.slug.trim() || !form.feed_url.trim()) {
      return toast.error('Name, slug and feed URL are required');
    }
    setSaving(true);
    try {
      if (isEdit) {
        // Slug is the natural key for sources; backend PATCH doesn't allow editing it
        const { slug, ...rest } = form;
        await axios.patch(`/api/admin/news/sources/${source.id}`, rest, authH());
      } else {
        await axios.post('/api/admin/news/sources', form, authH());
      }
      toast.success(isEdit ? 'Source updated' : 'Source added');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  }

  return (
    <div className="card" style={{ marginBottom:14, padding:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12 }}>
        <div style={{ fontWeight:700, fontSize:13 }}>{isEdit ? `Edit ${source.name}` : 'Add New Source'}</div>
        <button onClick={onClose} style={{ background:'transparent', border:'none', cursor:'pointer', color:'var(--text-muted)' }}>
          <X size={14} />
        </button>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
        <Field label="Name *">
          <input className="input-field" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="AdExchanger" />
        </Field>
        <Field label="Slug *" hint="lowercase, no spaces — used in URLs">
          <input className="input-field" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} placeholder="adexchanger" disabled={isEdit} />
        </Field>
        <Field label="Feed URL *" hint="RSS or Atom feed URL">
          <input className="input-field" value={form.feed_url} onChange={e => setForm({ ...form, feed_url: e.target.value })} placeholder="https://example.com/feed/" />
        </Field>
        <Field label="Site URL" hint="The publisher's homepage (optional)">
          <input className="input-field" value={form.site_url} onChange={e => setForm({ ...form, site_url: e.target.value })} placeholder="https://example.com" />
        </Field>
        <Field label="Logo URL" hint="Fallback thumbnail for articles without images">
          <input className="input-field" value={form.logo_url} onChange={e => setForm({ ...form, logo_url: e.target.value })} placeholder="https://example.com/logo.png" />
        </Field>
        <Field label="Active">
          <div style={{ paddingTop:6 }}>
            <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, cursor:'pointer' }}>
              <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} />
              Enabled (will be fetched every cycle)
            </label>
          </div>
        </Field>
      </div>
      <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:14 }}>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? <><RefreshCw size={11} style={{ animation:'spin 1s linear infinite' }}/> Saving…</> : <><Save size={12}/> {isEdit ? 'Save Changes' : 'Add Source'}</>}
        </button>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label style={{ fontSize:11, color:'var(--text-muted)', display:'block', marginBottom:4 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:3 }}>{hint}</div>}
    </div>
  );
}

// ─── Articles curation tab ───────────────────────────────────────────────────
function ArticlesTab() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState({ q:'', hidden:'false', featured:'' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filter.q)        params.q = filter.q;
      if (filter.hidden)   params.hidden = filter.hidden;
      if (filter.featured) params.featured = filter.featured;
      const { data } = await axios.get('/api/admin/news/articles', { params, ...authH() });
      setArticles(data);
    } catch { toast.error('Failed to load articles'); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  async function toggleFeature(a) {
    try {
      await axios.patch(`/api/admin/news/articles/${a.id}/feature`, { featured: !a.is_featured }, authH());
      toast.success(a.is_featured ? 'Unpinned' : 'Pinned as Editor\u2019s Pick');
      load();
    } catch { toast.error('Failed'); }
  }
  async function toggleHide(a) {
    try {
      await axios.patch(`/api/admin/news/articles/${a.id}/hide`, { hidden: !a.is_hidden }, authH());
      toast.success(a.is_hidden ? 'Article restored' : 'Article hidden');
      load();
    } catch { toast.error('Failed'); }
  }

  return (
    <div>
      <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
        <input
          className="input-field"
          placeholder="Search title or summary…"
          value={filter.q}
          onChange={e => setFilter(f => ({ ...f, q: e.target.value }))}
          style={{ minWidth:220, flex:'1 1 220px', maxWidth:360 }}
        />
        <select className="select-field" value={filter.hidden} onChange={e => setFilter(f => ({ ...f, hidden: e.target.value }))}>
          <option value="false">Visible only</option>
          <option value="true">Hidden only</option>
          <option value="">All</option>
        </select>
        <select className="select-field" value={filter.featured} onChange={e => setFilter(f => ({ ...f, featured: e.target.value }))}>
          <option value="">All</option>
          <option value="true">Featured only</option>
        </select>
        <button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={11}/> Refresh</button>
      </div>

      {loading ? <div style={{ textAlign:'center', padding:40 }}><span className="spinner"/></div> : (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Article</th>
                <th style={{ width:130 }}>Source</th>
                <th style={{ width:120 }}>Published</th>
                <th style={{ width:90, textAlign:'center' }}>Flags</th>
                <th style={{ width:160 }}></th>
              </tr>
            </thead>
            <tbody>
              {articles.map(a => (
                <tr key={a.id} style={{ opacity: a.is_hidden ? 0.45 : 1 }}>
                  <td>
                    <div style={{ fontWeight:600, fontSize:12, marginBottom:2 }}>
                      <a href={a.url} target="_blank" rel="noopener noreferrer" style={{ color:'var(--text-primary)', textDecoration:'none' }}>
                        {a.title} <ExternalLink size={9} style={{ verticalAlign:'middle', color:'var(--text-muted)' }}/>
                      </a>
                    </div>
                    {a.categories?.length > 0 && (
                      <div style={{ display:'flex', gap:3, flexWrap:'wrap' }}>
                        {a.categories.slice(0, 5).map(c => (
                          <span key={c} className="badge badge-muted" style={{ fontSize:9, padding:'1px 5px' }}>{c}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize:11, color:'var(--cyan)' }}>{a.source_name}</td>
                  <td style={{ fontSize:11, color:'var(--text-muted)' }}>{new Date(a.published_at).toLocaleString()}</td>
                  <td style={{ textAlign:'center' }}>
                    <div style={{ display:'flex', gap:3, justifyContent:'center' }}>
                      {a.is_featured && <span className="badge badge-amber" style={{ fontSize:9, padding:'1px 5px' }} title="Editor's Pick"><Star size={8}/></span>}
                      {a.is_trending && <span className="badge badge-red"   style={{ fontSize:9, padding:'1px 5px' }} title="Trending"><Activity size={8}/></span>}
                      {a.is_hidden   && <span className="badge badge-muted" style={{ fontSize:9, padding:'1px 5px' }} title="Hidden"><EyeOff size={8}/></span>}
                    </div>
                  </td>
                  <td>
                    <div style={{ display:'flex', gap:4, justifyContent:'flex-end' }}>
                      <button
                        className={`btn btn-sm ${a.is_featured ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => toggleFeature(a)}
                        title={a.is_featured ? 'Unpin' : 'Pin as Editor\u2019s Pick'}
                      >
                        <Star size={10}/>
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => toggleHide(a)}
                        title={a.is_hidden ? 'Restore' : 'Hide'}
                      >
                        {a.is_hidden ? <Eye size={10}/> : <EyeOff size={10}/>}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {articles.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>No articles match</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Fetch log tab ───────────────────────────────────────────────────────────
function FetchLogTab() {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get('/api/admin/news/fetch-log', authH()); setLogs(data); }
    catch { toast.error('Failed to load log'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:14 }}>
        <button className="btn btn-secondary btn-sm" onClick={load}><RefreshCw size={11}/> Refresh</button>
      </div>
      {loading ? <div style={{ textAlign:'center', padding:40 }}><span className="spinner"/></div> : (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width:50, textAlign:'center' }}>Status</th>
                <th>Source</th>
                <th style={{ width:80, textAlign:'center' }}>Added</th>
                <th style={{ width:80, textAlign:'center' }}>Skipped</th>
                <th>Error</th>
                <th style={{ width:160 }}>Completed</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(l => (
                <tr key={l.id}>
                  <td style={{ textAlign:'center' }}>
                    {l.status === 'ok'
                      ? <CheckCircle size={14} style={{ color:'var(--green)' }}/>
                      : <AlertCircle size={14} style={{ color:'var(--red)' }}/>}
                  </td>
                  <td style={{ fontSize:12 }}>{l.source_name || <span style={{ color:'var(--text-muted)' }}>—</span>}</td>
                  <td style={{ textAlign:'center', fontFamily:'var(--font-mono)', fontSize:11, color: l.articles_added > 0 ? 'var(--green)' : 'var(--text-muted)' }}>
                    +{l.articles_added}
                  </td>
                  <td style={{ textAlign:'center', fontFamily:'var(--font-mono)', fontSize:11, color:'var(--text-muted)' }}>
                    {l.articles_skipped}
                  </td>
                  <td style={{ fontSize:11, color:'var(--red)', maxWidth:320, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={l.error_message || ''}>
                    {l.error_message || ''}
                  </td>
                  <td style={{ fontSize:11, color:'var(--text-muted)' }}>{new Date(l.completed_at).toLocaleString()}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign:'center', padding:40, color:'var(--text-muted)' }}>No fetch runs yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
